import { type DateRange } from '@dev-spendesk/grapes';
import type { Filter, ValueFilter } from '@spendesk/bookkeeping-core-types';
import type * as D from '@spendesk/general-type-helpers/Date';
import * as NEA from '@spendesk/general-type-helpers/NonEmptyArray';
import { subDays, format } from 'date-fns';
import gql from 'graphql-tag';
import { useQueryClient } from 'react-query';
import { v4 as uuid4 } from 'uuid';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { getExportPeriods } from 'modules/bookkeep/export/graphql/utils/payableHelpers';
import { AvailableAccountingIntegrationsApi } from 'modules/bookkeep/hooks/';
import {
  useAccountingIntegrationHistory,
  type Result as AccountingIntegrationHistoryResult,
} from 'modules/bookkeep/hooks/useAccountingIntegrationHistory';
import { useQuery } from 'src/core/api/hooks/useQuery';
import { type QueryState } from 'src/core/api/queryState';

import { getExportPayablesCountQueryKey } from './query-key-registry';

/**
 * Query and cache config
 */
const GET_PAYABLES_TO_EXPORT_COUNT = gql`
  query Id42c6ed6de9882d116b7558fd66e4ebce(
    $companyId: ID!
    $unexportedPayablesFilter: JSON!
    $unpreparedPayablesFilter: JSON!
    $exportedPayablesFilter: JSON!
    $previousPeriodPayablesFilter: JSON!
  ) {
    __typename
  }
`;

export type ExportPayablesCountQueryState = QueryState<
  ExportPayablesCountResponse,
  unknown
>;

export type ExportPayablesCountResponse = {
  payablesCounts: {
    unexportedPayablesCount: number;
    unpreparedPayablesCount: number;
    exportedPayablesCount: number;
    previousPeriodUnexportedPayablesCount: number;
  };
};

export type ExportPayablesCountVariables = {
  companyId: string;
} & PayablesCountsFilters;

export const useInvalidateExportPayablesCountQueryCache = () => {
  const queryClient = useQueryClient();

  return async (companyId: string, period: DateRange): Promise<void> => {
    await queryClient.invalidateQueries(
      getExportPayablesCountQueryKey(companyId, period),
    );
  };
};

const getQueryConfig = (options?: {
  companyId: string;
  period: DateRange;
  availableAccountingIntegrations: AvailableAccountingIntegrationsApi.AvailableAccountingIntegrationsResponse;
  accountingIntegrationHistory: AccountingIntegrationHistoryResult;
}) => {
  const variables = options
    ? {
        companyId: options.companyId,
        ...getPayablesCountsFilters(
          options.period,
          options.availableAccountingIntegrations,
          options.accountingIntegrationHistory,
        ),
      }
    : undefined;

  return {
    type: 'graphQL',
    target: 'v2',
    query: GET_PAYABLES_TO_EXPORT_COUNT,
    variables,
  } as const;
};

/**
 * Query hook
 */
export const useExportPayablesCount = (period: DateRange) => {
  const companyId = useCompanyId();
  const idempotencyKey = uuid4();

  const availableAccountingIntegrationsQueryResult =
    AvailableAccountingIntegrationsApi.useAvailableAccountingIntegrations(
      idempotencyKey,
    );

  const accountingIntegrationHistoryQueryResult =
    useAccountingIntegrationHistory();

  if (
    availableAccountingIntegrationsQueryResult.status === 'error' ||
    accountingIntegrationHistoryQueryResult.status === 'error'
  ) {
    throw new Error('Can not load dependencies');
  }

  const availableAccountingIntegrations =
    availableAccountingIntegrationsQueryResult.status === 'success'
      ? availableAccountingIntegrationsQueryResult.data
      : undefined;

  const accountingIntegrationHistory =
    accountingIntegrationHistoryQueryResult.status === 'success'
      ? accountingIntegrationHistoryQueryResult.data
      : undefined;

  const isNewBookkeepingDependenciesLoaded = !!(
    availableAccountingIntegrations && accountingIntegrationHistory
  );

  const isEnabled =
    isNewBookkeepingDependenciesLoaded &&
    period.every((date) => date !== undefined);

  const reshapeData = reshapePayablesCounts;

  return useQuery({
    key: getExportPayablesCountQueryKey(companyId, period),
    request: getQueryConfig(
      isEnabled
        ? {
            companyId,
            period,
            availableAccountingIntegrations,
            accountingIntegrationHistory,
          }
        : undefined,
    ),
    isEnabled,
    reshapeData,
  });
};

/**
 * Query reshapers
 */

export type ExportPayablesCountRawResponse = {
  company: {
    unexportedPayables: { totalCount: number };
    unpreparedPayables: { totalCount: number };
    exportedPayables: { totalCount: number };
    previousPeriodPayables: { totalCount: number };
  };
};

const reshapePayablesCounts = ({
  company: {
    unexportedPayables: { totalCount: unexportedPayablesCount },
    unpreparedPayables: { totalCount: unpreparedPayablesCount },
    exportedPayables: { totalCount: exportedPayablesCount },
    previousPeriodPayables: {
      totalCount: previousPeriodUnexportedPayablesCount,
    },
  },
}: ExportPayablesCountRawResponse): ExportPayablesCountResponse => ({
  payablesCounts: {
    unexportedPayablesCount,
    unpreparedPayablesCount,
    exportedPayablesCount,
    previousPeriodUnexportedPayablesCount,
  },
});

type PayablesCountsFilters = {
  unexportedPayablesFilter: Filter;
  unpreparedPayablesFilter: Filter;
  exportedPayablesFilter: Filter;
  previousPeriodPayablesFilter: Filter;
};

export const getPayablesCountsFilters = (
  period: DateRange,
  availableAccountingIntegrations: AvailableAccountingIntegrationsApi.AvailableAccountingIntegrationsResponse,
  accountingIntegrationHistory: AccountingIntegrationHistoryResult,
): PayablesCountsFilters => {
  const [start, end] = period as Date[];

  const lastDayOfPreviousPeriod = subDays(start, 1);

  const exportPeriodFilters: Filter[] = [];
  const exportPeriods = getExportPeriods({
    selectedPeriod: period,
    availableAccountingIntegrations,
    accountingIntegrationHistory,
  });
  exportPeriods.forEach((exportPeriod) => {
    if (exportPeriod.from && exportPeriod.to) {
      exportPeriodFilters.push({
        field: 'firstExportedAt',
        operator: 'between',
        value: {
          // FIXME: the type here is wrong, but we don't have a serialized Filter type we can use to correctly type the dates
          from: format(
            exportPeriod.from,
            'yyyy-MM-dd',
          ) as unknown as D.ValidDate,
          // FIXME: the type here is wrong, but we don't have a serialized Filter type we can use to correctly type the dates
          to: format(exportPeriod.to, 'yyyy-MM-dd') as unknown as D.ValidDate,
        },
      });
    } else if (exportPeriod.from) {
      exportPeriodFilters.push({
        field: 'firstExportedAt',
        operator: 'after',
        value: format(
          exportPeriod.from,
          'yyyy-MM-dd',
          // FIXME: the type here is wrong, but we don't have a serialized Filter type we can use to correctly type the dates
        ) as unknown as D.ValidDate,
      });
    } else if (exportPeriod.to) {
      exportPeriodFilters.push({
        field: 'firstExportedAt',
        operator: 'before',
        // FIXME: the type here is wrong, but we don't have a serialized Filter type we can use to correctly type the dates
        value: format(exportPeriod.to, 'yyyy-MM-dd') as unknown as D.ValidDate,
      });
    }
  });

  const payableDateFilter: ValueFilter = {
    field: 'payableDate',
    operator: 'between',
    value: {
      // FIXME: the type here is wrong, but we don't have a serialized Filter type we can use to correctly type the dates
      from: format(start, 'yyyy-MM-dd') as unknown as D.ValidDate,
      // FIXME: the type here is wrong, but we don't have a serialized Filter type we can use to correctly type the dates
      to: format(end, 'yyyy-MM-dd') as unknown as D.ValidDate,
    },
  };

  const exportedPayablesFilter: Filter = {
    operator: 'and',
    subfilters: [
      payableDateFilter,
      {
        field: 'bookkeepingStatus',
        operator: '=',
        value: NEA.fromSingleItem('exported'),
      },
      ...(exportPeriodFilters.length
        ? [
            {
              operator: 'or' as const,
              subfilters: exportPeriodFilters,
            },
          ]
        : []),
    ],
  };

  return {
    unexportedPayablesFilter: {
      operator: 'and',
      subfilters: [
        payableDateFilter,
        {
          field: 'bookkeepingStatus',
          operator: '=',
          value: NEA.fromSingleItem('toExport'),
        },
      ],
    },
    unpreparedPayablesFilter: {
      operator: 'and',
      subfilters: [
        payableDateFilter,
        {
          field: 'bookkeepingStatus',
          operator: '=',
          value: NEA.fromSingleItem('toPrepare'),
        },
        {
          field: 'withBookkeepingStartDate',
          operator: '=',
          value: true,
        },
      ],
    },
    exportedPayablesFilter,
    previousPeriodPayablesFilter: {
      operator: 'and',
      subfilters: [
        {
          field: 'payableDate',
          operator: 'before',
          // FIXME: the type here is wrong, but we don't have a serialized Filter type we can use to correctly type the dates
          value: format(
            lastDayOfPreviousPeriod,
            'yyyy-MM-dd',
          ) as unknown as D.ValidDate,
        },
        {
          field: 'bookkeepingStatus',
          operator: '=',
          value: NEA.fromSingleItem('toExport'),
        },
        {
          field: 'withBookkeepingStartDate',
          operator: '=',
          value: true,
        },
      ],
    },
  };
};
